import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Plus, Trash2, GripVertical, Save, Eye, Edit2, Layers, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Block types definitions
const BLOCK_TYPES = [
  { value: 'hero', label: 'Hero (banner principal)', defaultData: () => ({ title: 'Título principal', subtitle: '', description: '', cta_text: 'Explorar', cta_link: '/courses' }) },
  { value: 'features', label: 'Características (cards)', defaultData: () => ({ title: '¿Por qué elegirnos?', items: [{ icon: 'Award', title: 'Característica 1', description: 'Descripción' }] }) },
  { value: 'stats', label: 'Estadísticas (números)', defaultData: () => ({ title: 'Nuestros números', items: [{ value: '500+', label: 'Estudiantes' }] }) },
  { value: 'rich_text', label: 'Texto enriquecido (HTML)', defaultData: () => ({ title: 'Encabezado', content: '<p>Contenido HTML</p>' }) },
  { value: 'cta', label: 'CTA (llamado a acción)', defaultData: () => ({ title: '¿Listo para empezar?', subtitle: 'Únete hoy', button_text: 'Inscríbete', button_link: '/register' }) },
  { value: 'about_iaasp', label: 'Sobre IAASP', defaultData: () => ({}) },
  { value: 'latest_courses', label: 'Últimos cursos (auto)', defaultData: () => ({}) },
  { value: 'latest_articles', label: 'Últimos artículos (auto)', defaultData: () => ({}) },
  { value: 'contact_info', label: 'Información de contacto', defaultData: () => ({ title: 'Contáctanos', email: 'info@iaaspacademy.com', phone: '', address: '' }) },
];

const SortableBlockRow = ({ block, index, onEdit, onDelete, onMove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const typeMeta = BLOCK_TYPES.find(t => t.value === block.type);
  const previewTitle = block.data?.title || typeMeta?.label || block.type;

  return (
    <div ref={setNodeRef} style={style} className={`${isDragging ? 'shadow-xl ring-2 ring-primary' : ''}`}>
      <Card data-testid={`block-row-${block.id}`}>
        <CardContent className="p-3 flex items-center gap-3">
          <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded" data-testid={`drag-handle-${block.id}`}>
            <GripVertical className="w-4 h-4 text-muted-foreground" />
          </button>
          <Badge variant="outline" className="font-mono text-xs">{block.type}</Badge>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate">{previewTitle}</p>
            <p className="text-xs text-muted-foreground">Orden: {index + 1}</p>
          </div>
          <div className="flex gap-1 shrink-0">
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => onMove(index, -1)} disabled={index === 0} data-testid={`move-up-${block.id}`}>
              <ChevronUp className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => onMove(index, 1)} data-testid={`move-down-${block.id}`}>
              <ChevronDown className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="outline" onClick={() => onEdit(block)} data-testid={`edit-block-${block.id}`}>
              <Edit2 className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => onDelete(block.id)} data-testid={`delete-block-${block.id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const BlockEditor = ({ block, onChange }) => {
  const data = block.data || {};
  const upd = (k, v) => onChange({ ...block, data: { ...data, [k]: v } });

  switch (block.type) {
    case 'hero':
      return (
        <div className="space-y-3">
          <div><Label>Título</Label><Input value={data.title || ''} onChange={e => upd('title', e.target.value)} data-testid="hero-title-input" /></div>
          <div><Label>Subtítulo</Label><Input value={data.subtitle || ''} onChange={e => upd('subtitle', e.target.value)} /></div>
          <div><Label>Descripción</Label><Textarea rows={3} value={data.description || ''} onChange={e => upd('description', e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>CTA texto</Label><Input value={data.cta_text || ''} onChange={e => upd('cta_text', e.target.value)} /></div>
            <div><Label>CTA enlace</Label><Input value={data.cta_link || ''} onChange={e => upd('cta_link', e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>CTA secundario texto</Label><Input value={data.cta_secondary_text || ''} onChange={e => upd('cta_secondary_text', e.target.value)} /></div>
            <div><Label>CTA secundario enlace</Label><Input value={data.cta_secondary_link || ''} onChange={e => upd('cta_secondary_link', e.target.value)} /></div>
          </div>
        </div>
      );
    case 'rich_text':
      return (
        <div className="space-y-3">
          <div><Label>Título</Label><Input value={data.title || ''} onChange={e => upd('title', e.target.value)} /></div>
          <div><Label>Contenido (HTML)</Label><Textarea rows={8} value={data.content || ''} onChange={e => upd('content', e.target.value)} className="font-mono text-xs" /></div>
        </div>
      );
    case 'cta':
      return (
        <div className="space-y-3">
          <div><Label>Título</Label><Input value={data.title || ''} onChange={e => upd('title', e.target.value)} /></div>
          <div><Label>Subtítulo</Label><Input value={data.subtitle || ''} onChange={e => upd('subtitle', e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Botón texto</Label><Input value={data.button_text || ''} onChange={e => upd('button_text', e.target.value)} /></div>
            <div><Label>Botón enlace</Label><Input value={data.button_link || ''} onChange={e => upd('button_link', e.target.value)} /></div>
          </div>
        </div>
      );
    case 'features':
    case 'stats': {
      const items = Array.isArray(data.items) ? data.items : [];
      const updItem = (i, k, v) => {
        const next = items.map((it, idx) => idx === i ? { ...it, [k]: v } : it);
        upd('items', next);
      };
      const addItem = () => upd('items', [...items, block.type === 'stats' ? { value: '', label: '' } : { icon: 'Award', title: '', description: '' }]);
      const delItem = (i) => upd('items', items.filter((_, idx) => idx !== i));
      return (
        <div className="space-y-3">
          <div><Label>Título</Label><Input value={data.title || ''} onChange={e => upd('title', e.target.value)} /></div>
          <div className="space-y-2">
            <Label>Items</Label>
            {items.map((item, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end p-2 border rounded-lg" data-testid={`item-row-${i}`}>
                {block.type === 'stats' ? (
                  <>
                    <Input className="col-span-3" placeholder="Valor (500+)" value={item.value || ''} onChange={e => updItem(i, 'value', e.target.value)} />
                    <Input className="col-span-8" placeholder="Etiqueta" value={item.label || ''} onChange={e => updItem(i, 'label', e.target.value)} />
                  </>
                ) : (
                  <>
                    <Input className="col-span-2" placeholder="Icon" value={item.icon || ''} onChange={e => updItem(i, 'icon', e.target.value)} />
                    <Input className="col-span-3" placeholder="Título" value={item.title || ''} onChange={e => updItem(i, 'title', e.target.value)} />
                    <Input className="col-span-6" placeholder="Descripción" value={item.description || ''} onChange={e => updItem(i, 'description', e.target.value)} />
                  </>
                )}
                <Button size="sm" variant="ghost" className="col-span-1 text-destructive" onClick={() => delItem(i)} data-testid={`del-item-${i}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={addItem} data-testid="add-item-btn"><Plus className="w-4 h-4 mr-1" />Añadir item</Button>
          </div>
        </div>
      );
    }
    case 'contact_info':
      return (
        <div className="space-y-3">
          <div><Label>Título</Label><Input value={data.title || ''} onChange={e => upd('title', e.target.value)} /></div>
          <div><Label>Email</Label><Input value={data.email || ''} onChange={e => upd('email', e.target.value)} /></div>
          <div><Label>Teléfono</Label><Input value={data.phone || ''} onChange={e => upd('phone', e.target.value)} /></div>
          <div><Label>Dirección</Label><Input value={data.address || ''} onChange={e => upd('address', e.target.value)} /></div>
          <div><Label>Descripción</Label><Textarea rows={2} value={data.description || ''} onChange={e => upd('description', e.target.value)} /></div>
        </div>
      );
    default:
      return (
        <p className="text-sm text-muted-foreground p-4 bg-secondary rounded-lg">
          Este bloque tipo <code>{block.type}</code> usa datos automáticos. No requiere edición manual.
        </p>
      );
  }
};

const PageBuilder = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { slug = 'home' } = useParams();
  const [page, setPage] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // block being edited
  const [addOpen, setAddOpen] = useState(false);
  const [newType, setNewType] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    if (user && !['superadmin', 'admin'].includes(user.role)) {
      navigate('/dashboard');
      return;
    }
    load();
  }, [slug, user]);

  const load = async () => {
    try {
      const res = await api.get(`/pages/${slug}`);
      setPage(res.data);
      const initial = (res.data?.content?.blocks || []).map((b, i) => ({ ...b, order: b.order ?? i + 1 }));
      setBlocks(initial.sort((a, b) => a.order - b.order));
    } catch {
      // page doesn't exist yet
      setPage({ slug, title: slug.charAt(0).toUpperCase() + slug.slice(1), content: { blocks: [] } });
      setBlocks([]);
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const ordered = blocks.map((b, i) => ({ ...b, order: i + 1 }));
      await api.put(`/pages/${slug}/blocks`, { title: page?.title || slug, content: { blocks: ordered } });
      toast.success('Página guardada');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = blocks.findIndex(b => b.id === active.id);
    const newIdx = blocks.findIndex(b => b.id === over.id);
    setBlocks(arrayMove(blocks, oldIdx, newIdx));
  };

  const moveBlock = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= blocks.length) return;
    setBlocks(arrayMove(blocks, index, target));
  };

  const addBlock = () => {
    const meta = BLOCK_TYPES.find(t => t.value === newType);
    if (!meta) return;
    const newBlock = {
      id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2),
      type: newType,
      order: blocks.length + 1,
      data: meta.defaultData(),
    };
    setBlocks([...blocks, newBlock]);
    setAddOpen(false);
    setNewType('');
    setEditing(newBlock);
  };

  const updateBlock = (updated) => setBlocks(blocks.map(b => b.id === updated.id ? updated : b));
  const deleteBlock = (id) => setBlocks(blocks.filter(b => b.id !== id));

  if (loading) return <div className="p-8">Cargando editor...</div>;

  return (
    <div className="p-6 md:p-12 bg-secondary min-h-screen" data-testid="page-builder">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold mb-2">
              <Layers className="w-3.5 h-3.5" /> CMS Page Builder
            </div>
            <h1 className="text-3xl font-heading font-bold text-primary">Editar página: <span className="text-accent">{slug}</span></h1>
            <p className="text-muted-foreground">Arrastra para reordenar bloques · Haz clic en lápiz para editar contenido.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => window.open(slug === 'home' ? '/' : `/${slug}`, '_blank')} className="gap-2" data-testid="preview-btn">
              <Eye className="w-4 h-4" /> Vista previa
            </Button>
            <Button onClick={save} disabled={saving} className="gap-2" data-testid="save-page-btn">
              <Save className="w-4 h-4" /> {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </div>

        <Card className="mb-4">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Bloques ({blocks.length})</CardTitle>
              <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="gap-2" data-testid="add-block-btn">
                    <Plus className="w-4 h-4" /> Añadir bloque
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Nuevo bloque</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <Label>Tipo de bloque</Label>
                    <Select value={newType} onValueChange={setNewType}>
                      <SelectTrigger data-testid="block-type-select"><SelectValue placeholder="Selecciona un tipo..." /></SelectTrigger>
                      <SelectContent>
                        {BLOCK_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setAddOpen(false)}>Cancelar</Button>
                    <Button onClick={addBlock} disabled={!newType} data-testid="confirm-add-block">Añadir</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {blocks.length === 0 ? (
              <p className="text-center py-12 text-muted-foreground">No hay bloques. Pulsa "Añadir bloque" para empezar.</p>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={blocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-2">
                    {blocks.map((block, i) => (
                      <SortableBlockRow
                        key={block.id}
                        block={block}
                        index={i}
                        onEdit={setEditing}
                        onDelete={deleteBlock}
                        onMove={moveBlock}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </CardContent>
        </Card>

        {editing && (
          <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
            <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Editar bloque · <span className="font-mono text-sm">{editing.type}</span></DialogTitle>
              </DialogHeader>
              <BlockEditor block={editing} onChange={(b) => setEditing(b)} />
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
                <Button onClick={() => { updateBlock(editing); setEditing(null); toast.success('Bloque actualizado (recuerda guardar)'); }} data-testid="save-block-btn">
                  Aplicar cambios
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
};

export default PageBuilder;
